import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import { useAuth } from '../../context/AuthContext';
import { Plus, Building2, Send, Check, ThumbsDown, FileCheck, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const FINANCE_ROLES = ['Super Admin', 'Secretary', 'Director'];

const STATUS_COLORS = {
  Draft: 'bg-gray-100 text-gray-600',
  Submitted: 'bg-yellow-100 text-yellow-700',
  Approved: 'bg-blue-100 text-blue-700',
  Posted: 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700',
  Cancelled: 'bg-gray-100 text-gray-500',
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const NewVendorModal = ({ onClose, onDone }) => {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await accountsApi.post('/vendors', { code, name, contact_person: contactPerson || undefined, phone: phone || undefined });
      toast.success('Vendor created');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create vendor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center">
          <h2 className="text-lg font-bold">New Vendor</h2>
          <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Code</label>
            <input required type="text" value={code} onChange={(e) => setCode(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
            <input required type="text" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Contact Person</label>
            <input type="text" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
            <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
              {saving ? 'Saving...' : 'Create Vendor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const emptyLine = () => ({ account_id: '', description: '', amount: '' });

const NewBillModal = ({ vendor, expenseAccounts, onClose, onDone }) => {
  const [billNo, setBillNo] = useState('');
  const [billDate, setBillDate] = useState(todayISO());
  const [purpose, setPurpose] = useState('');
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);

  const updateLine = (i, field, value) => setLines(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  const addLine = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (i) => setLines(prev => prev.filter((_, idx) => idx !== i));
  const total = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lines.some(l => !l.account_id || !(Number(l.amount) > 0))) {
      return toast.error('Every line needs a category and an amount greater than 0');
    }
    setSaving(true);
    try {
      await accountsApi.post('/vendor-bills', {
        vendor_id: vendor.id, bill_no: billNo || undefined, bill_date: billDate, purpose: purpose || undefined,
        lines: lines.map(l => ({ account_id: Number(l.account_id), description: l.description || undefined, amount: Number(l.amount) })),
      });
      toast.success('Bill created as Draft');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create bill');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-bold">New Bill — {vendor.code} {vendor.name}</h2>
          <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Vendor's Invoice # (optional)</label>
              <input type="text" value={billNo} onChange={(e) => setBillNo(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Bill Date</label>
              <input required type="date" value={billDate} onChange={(e) => setBillDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Purpose</label>
              <input type="text" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="Brief description of this bill"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">Lines</h3>
              <button type="button" onClick={addLine} className="flex items-center gap-1 text-blue-700 hover:text-blue-900 text-sm font-medium">
                <Plus size={14} /> Add Line
              </button>
            </div>
            {lines.map((line, i) => (
              <div key={i} className="grid gap-2 mb-2 items-start" style={{ gridTemplateColumns: '2fr 2fr 1fr auto' }}>
                <select required value={line.account_id} onChange={(e) => updateLine(i, 'account_id', e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Category...</option>
                  {expenseAccounts.map(a => <option key={a.id} value={a.id}>{a.account_code} — {a.account_name}</option>)}
                </select>
                <input type="text" placeholder="Description" value={line.description} onChange={(e) => updateLine(i, 'description', e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <input required type="number" step="0.01" min="0.01" placeholder="Amount" value={line.amount} onChange={(e) => updateLine(i, 'amount', e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <button type="button" onClick={() => removeLine(i)} disabled={lines.length <= 1}
                  className="text-red-500 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed p-2">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <div className="text-sm font-medium text-gray-700 mt-2">Total: {total.toFixed(2)}</div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
              {saving ? 'Saving...' : 'Create as Draft'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BillsPanel = ({ vendor, isFinance, expenseAccounts, onClose, onVendorChanged }) => {
  const [bills, setBills] = useState(null);
  const [showNewBill, setShowNewBill] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [reportRows, setReportRows] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const fetchBills = async () => {
    try {
      const res = await accountsApi.get('/vendor-bills', { params: { vendor_id: vendor.id } });
      setBills(res.data.vendor_bills);
    } catch (err) {
      toast.error('Failed to load bills');
    }
  };

  useEffect(() => { fetchBills(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runAction = async (billId, fn, successMsg) => {
    setBusyId(billId);
    try {
      await fn();
      toast.success(successMsg);
      await fetchBills();
      onVendorChanged();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    } finally {
      setBusyId(null);
      setRejectingId(null);
      setRejectReason('');
    }
  };

  const handleSubmitBill = (id) => runAction(id, () => accountsApi.put(`/vendor-bills/${id}/submit`), 'Bill submitted');
  const handleApproveBill = (id) => runAction(id, () => accountsApi.put(`/vendor-bills/${id}/approve`), 'Bill approved');
  const handlePostBill = (id) => runAction(id, () => accountsApi.put(`/vendor-bills/${id}/post`), 'Bill posted');
  const handleRejectBill = (id) => {
    if (!rejectReason.trim()) return toast.error('Reason is required');
    runAction(id, () => accountsApi.put(`/vendor-bills/${id}/reject`, { rejection_reason: rejectReason }), 'Bill rejected');
  };

  const runActivityReport = async () => {
    const inRange = (bills || []).filter(b => b.status === 'Posted'
      && (!reportFrom || b.bill_date.slice(0, 10) >= reportFrom)
      && (!reportTo || b.bill_date.slice(0, 10) <= reportTo));
    setReportLoading(true);
    try {
      const details = await Promise.all(inRange.map(b => accountsApi.get(`/vendor-bills/${b.id}`)));
      const byAccount = new Map();
      for (const res of details) {
        for (const line of res.data.lines) {
          const key = line.account_code;
          const existing = byAccount.get(key) || { account_code: line.account_code, account_name: line.account_name, total: 0 };
          existing.total += Number(line.amount);
          byAccount.set(key, existing);
        }
      }
      setReportRows([...byAccount.values()].sort((a, b) => a.account_code.localeCompare(b.account_code)));
    } catch (err) {
      toast.error('Failed to build activity report');
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-bold">{vendor.code} — {vendor.name}</h2>
          <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-semibold text-gray-700">Bills</h3>
            <button onClick={() => setShowNewBill(true)} className="flex items-center gap-1 bg-blue-800 text-white px-3 py-2 rounded-lg text-xs hover:bg-blue-900">
              <Plus size={14} /> New Bill
            </button>
          </div>

          {bills === null ? (
            <div className="text-center py-6 text-gray-500 text-sm">Loading...</div>
          ) : bills.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No bills yet</div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="border-b border-gray-200">
                    <th className="text-left px-3 py-2 text-gray-600 font-semibold">Date</th>
                    <th className="text-left px-3 py-2 text-gray-600 font-semibold">Invoice #</th>
                    <th className="text-left px-3 py-2 text-gray-600 font-semibold">Purpose</th>
                    <th className="text-right px-3 py-2 text-gray-600 font-semibold">Amount</th>
                    <th className="text-center px-3 py-2 text-gray-600 font-semibold">Status</th>
                    <th className="text-right px-3 py-2 text-gray-600 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map(b => (
                    <React.Fragment key={b.id}>
                      <tr className="border-t border-gray-100">
                        <td className="px-3 py-2 text-gray-700">{b.bill_date?.slice(0, 10)}</td>
                        <td className="px-3 py-2 text-gray-700">{b.bill_no || '—'}</td>
                        <td className="px-3 py-2 text-gray-700">{b.purpose || '—'}</td>
                        <td className="px-3 py-2 text-right text-gray-800">{Number(b.total_amount).toFixed(2)}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[b.status]}`}>{b.status}</span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex gap-1 justify-end flex-wrap">
                            {b.status === 'Draft' && (
                              <button onClick={() => handleSubmitBill(b.id)} disabled={busyId === b.id}
                                className="flex items-center gap-1 bg-blue-800 text-white px-2 py-1 rounded text-xs hover:bg-blue-900 disabled:opacity-50">
                                <Send size={12} /> Submit
                              </button>
                            )}
                            {b.status === 'Submitted' && isFinance && (
                              <>
                                <button onClick={() => handleApproveBill(b.id)} disabled={busyId === b.id}
                                  className="flex items-center gap-1 bg-green-700 text-white px-2 py-1 rounded text-xs hover:bg-green-800 disabled:opacity-50">
                                  <Check size={12} /> Approve
                                </button>
                                <button onClick={() => setRejectingId(rejectingId === b.id ? null : b.id)} disabled={busyId === b.id}
                                  className="flex items-center gap-1 bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700 disabled:opacity-50">
                                  <ThumbsDown size={12} /> Reject
                                </button>
                              </>
                            )}
                            {b.status === 'Approved' && isFinance && (
                              <button onClick={() => handlePostBill(b.id)} disabled={busyId === b.id}
                                className="flex items-center gap-1 bg-blue-800 text-white px-2 py-1 rounded text-xs hover:bg-blue-900 disabled:opacity-50">
                                <FileCheck size={12} /> Post
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {rejectingId === b.id && (
                        <tr className="border-t border-gray-100 bg-gray-50">
                          <td colSpan={6} className="px-3 py-2">
                            <div className="flex gap-2 items-center">
                              <input type="text" placeholder="Rejection reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                                className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                              <button onClick={() => handleRejectBill(b.id)} disabled={busyId === b.id}
                                className="bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700 disabled:opacity-50">Confirm Reject</button>
                            </div>
                          </td>
                        </tr>
                      )}
                      {b.rejection_reason && (
                        <tr className="border-t border-gray-50">
                          <td colSpan={6} className="px-3 py-1 text-xs text-red-600">Rejected: {b.rejection_reason}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Activity by Category</h3>
            <div className="flex flex-wrap items-end gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
                <input type="date" value={reportFrom} onChange={(e) => setReportFrom(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
                <input type="date" value={reportTo} onChange={(e) => setReportTo(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <button onClick={runActivityReport} disabled={reportLoading}
                className="bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900 disabled:opacity-50">
                {reportLoading ? 'Running...' : 'Run'}
              </button>
            </div>
            {reportRows && (
              reportRows.length === 0 ? (
                <div className="text-sm text-gray-500">No Posted bills in this range</div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr className="border-b border-gray-200">
                        <th className="text-left px-3 py-2 text-gray-600 font-semibold">Category</th>
                        <th className="text-right px-3 py-2 text-gray-600 font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportRows.map(r => (
                        <tr key={r.account_code} className="border-t border-gray-100">
                          <td className="px-3 py-2 text-gray-700">{r.account_code} — {r.account_name}</td>
                          <td className="px-3 py-2 text-right text-gray-800">{r.total.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        </div>
        <div className="flex gap-3 p-6 pt-0 flex-shrink-0">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Close</button>
        </div>
      </div>

      {showNewBill && (
        <NewBillModal vendor={vendor} expenseAccounts={expenseAccounts} onClose={() => setShowNewBill(false)}
          onDone={() => { setShowNewBill(false); fetchBills(); onVendorChanged(); }} />
      )}
    </div>
  );
};

const Vendors = () => {
  const { user } = useAuth();
  const [vendors, setVendors] = useState(null);
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [showNewVendor, setShowNewVendor] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const isFinance = FINANCE_ROLES.includes(user?.role);

  const fetchVendors = async () => {
    try {
      const res = await accountsApi.get('/vendors');
      setVendors(res.data.vendors);
    } catch (err) {
      toast.error('Failed to load vendors');
    }
  };

  useEffect(() => {
    fetchVendors();
    accountsApi.get('/chart-of-accounts')
      .then(res => setExpenseAccounts(res.data.accounts.filter(a => a.account_type === 'Expense' && a.is_active !== false)))
      .catch(() => toast.error('Failed to load categories'));
  }, []);

  return (
    <Layout title="Vendors">
      <div className="flex justify-end mb-4">
        {isFinance && (
          <button onClick={() => setShowNewVendor(true)} className="flex items-center gap-2 bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900">
            <Plus size={16} /> New Vendor
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Code</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Name</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Contact</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Total Billed</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Total Paid</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Outstanding</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Status</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {vendors === null ? (
              <tr><td colSpan={8} className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : vendors.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-gray-500">No vendors yet</td></tr>
            ) : vendors.map(v => (
              <tr key={v.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 font-mono text-gray-700">{v.code}</td>
                <td className="px-4 py-2 font-medium text-gray-800">{v.name}</td>
                <td className="px-4 py-2 text-gray-600">{v.contact_person || '—'}{v.phone ? ` — ${v.phone}` : ''}</td>
                <td className="px-4 py-2 text-right text-gray-800">{v.total_billed.toFixed(2)}</td>
                <td className="px-4 py-2 text-right text-gray-800">{v.total_paid.toFixed(2)}</td>
                <td className={`px-4 py-2 text-right font-semibold ${v.outstanding > 0 ? 'text-amber-600' : 'text-gray-800'}`}>{v.outstanding.toFixed(2)}</td>
                <td className="px-4 py-2 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${v.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {v.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => setSelectedVendor(v)} className="flex items-center gap-1 text-blue-700 hover:text-blue-900 text-sm font-medium justify-end w-full">
                    <Building2 size={14} /> Bills
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNewVendor && (
        <NewVendorModal onClose={() => setShowNewVendor(false)} onDone={() => { setShowNewVendor(false); fetchVendors(); }} />
      )}
      {selectedVendor && (
        <BillsPanel vendor={selectedVendor} isFinance={isFinance} expenseAccounts={expenseAccounts}
          onClose={() => setSelectedVendor(null)} onVendorChanged={fetchVendors} />
      )}
    </Layout>
  );
};

export default Vendors;
